/**
 * @description Helper variable to store the default filters for the company search.
 */
export const defaultFiltersCompany = {
  careerLevels: true,
  domains: true,
  lastActive: true,
  location: true,
  searchIn: true,
  display: true,
  spokenLanguages: true,
  studyLevels: true,
};


/**
 * @description This is a function to merge the filter values into a single string. Not only does it merge the values,
 * but it also translates the values if a translation function is provided. And most importantly, you can mix elements
 * with and without translations. If there's no translation for the key, the key will be returned as is.
 *
 * @param labels - an array of strings that should be merged into a single string (e.g. ['label1', 'label2', 'label3'])
 * @param transFunc - a translation function that takes a string and returns a string
 *
 * @example
 * const labels = ['label1', 'label2', 'label3'];
 * // returns 'label1; label2; label3'
 *
 * // with translation function
 * const labels = ['localize.key.1', 'localize.key.2', 'label3'];
 * // returns 'translatedLabel1; translatedLabel2; label3'
 *
 * // with translation function and additional values în double accolades (just like the string interpolation in next-i18next)
 * const labels = ['{{localize.key.1}} - some value', 'localize.key.2', 'label3'];
 * // returns 'translatedLabel1 - some value; translatedLabel2; label3'
 */
export const mergeFilterValues = (labels: (string[] | undefined)[], transFunc?: (label: string) => string): string | undefined => {
  const flatLabels = labels.flat();
  const validLabels = flatLabels.filter((label) => label !== undefined);

  const result = validLabels.map((label) => {
    if (transFunc && label) {
      let translatedLabel = label;

      // 1. regex for getting the string between double accolades
      // 2. check string if it contains {{}} and replace it with the transFunc
      const regex = /{{(.*?)}}/g;
      const matches = label.match(regex);

      // if regex matches are found, replace them with the transFunc
      if (matches) {
        matches.forEach((match) => {
          const key = match.replace('{{', '').replace('}}', '');
          const value = transFunc(key);
          translatedLabel = label.replace(match, value);
        });
      }

      return transFunc(translatedLabel);
    }

    return label;
  }).join('; ');

  return result === '' ? undefined : result;
};
